import { CardContent, Divider, Grid2, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PostRecordAndAttach from '@components/Attachments/PostRecordAndAttach';
import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import { DISCLOSURE_AUTH_CONSENT } from '@constants/consents';
import { ADD_INSURANCE } from '@constants/routes';

import {
  useCreateConsent,
  useGetConsent,
  useGetConsents,
} from '@hooks/api/consent';
import { useGetFHIRPatientWithAttachments } from '@hooks/api/fhir-patient';
import { useGetAllInsurance } from '@hooks/api/insurance';
import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';

import { Attachments } from '@layouts/DrilldownStyle';

import { FhirResource } from '@models/fhir-resource';
import { getDisclosureAuthConsentTemplate } from '@models/fhir-templates/consent';

const messages = defineMessages({
  insurance: {
    id: 'clientProfileCard.insurance',
    defaultMessage: 'Health Insurance',
  },
  insuranceIds: {
    id: 'clientProfileCard.insuranceIds',
    defaultMessage: 'Insurance IDs',
  },
  identityIds: {
    id: 'clientProfileCard.identityIds',
    defaultMessage: 'Identity IDs',
  },
  disclosureAuth: {
    id: 'clientProfileCard.disclosureAuthDocs',
    defaultMessage: 'Disclosure Authorization Documents',
  },
});

const IdsCard: React.FC<{
  patient_id?: string;
}> = ({ patient_id }) => {
  const { formatMessage } = useIntl();
  const { startAddingRecord } = useCuration();

  const isMobile = useIsMobile('sm');

  const { data: insuranceData, loading: insuranceLoading } = useGetAllInsurance(
    { patient_id }
  );
  const { data: consentsData, loading: consentsLoading } = useGetConsents();
  const { data: patientData, loading: patientLoading } =
    useGetFHIRPatientWithAttachments();
  const [createConsent, createConsentLoading] = useCreateConsent();
  const disclosureAuthConsent = consentsData.find((c) =>
    c.hasCategory(DISCLOSURE_AUTH_CONSENT)
  );
  const { data: disclosureAuthConsentWithAttachments } = useGetConsent({
    record_id: disclosureAuthConsent?.id ?? '',
    skip: !disclosureAuthConsent?.id,
  });

  const loading =
    insuranceLoading ||
    patientLoading ||
    consentsLoading ||
    createConsentLoading;

  const createAuthConsent = async () => {
    const response = await createConsent({
      input: {
        ...getDisclosureAuthConsentTemplate(patient_id ?? ''),
      },
      display_success: false,
    });
    const consentRecord = response.data?.postConsentRecord;
    return consentRecord ? new FhirResource(consentRecord) : null;
  };

  return (
    <ContentLoader loading={loading}>
      <CardContent>
        <Grid2 container spacing={3}>
          <Grid2 size={{ sm: 5.75, xs: 12 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">
                {formatMessage(messages.insuranceIds)}
              </Typography>
              <Icon
                name="plus"
                onClick={() => {
                  startAddingRecord();
                  navigate(ADD_INSURANCE);
                }}
                size="lg"
                type="fas"
              />
            </Stack>

            <Typography color="primary" variant="subtitle1">
              {formatMessage(messages.insurance)}
            </Typography>

            {insuranceData.length > 0 &&
              insuranceData.map((insurance) => {
                const { attachments, id, planNumber, provider } = insurance;
                return (
                  <Grid2 key={`insurance-${id}`} sx={{ marginBottom: '20px' }}>
                    <Typography
                      sx={{
                        fontSize: '16px',
                      }}
                    >
                      {provider} ({planNumber})
                    </Typography>

                    <Attachments
                      patient_id={patient_id}
                      attachments={attachments}
                      resource={insurance.getResourceId()}
                    />
                  </Grid2>
                );
              })}
          </Grid2>
          <Grid2 size={{ sm: 0.5, xs: 12 }}>
            <Divider orientation={isMobile ? 'horizontal' : 'vertical'} />
          </Grid2>
          <Grid2 size={{ sm: 5.75, xs: 12 }}>
            <Stack mb={2}>
              <Typography variant="h6">
                {formatMessage(messages.identityIds)}
              </Typography>

              <Attachments
                patient_id={patient_id}
                attachments={patientData?.attachments}
                resource={patientData?.getResourceId()}
              />
            </Stack>

            <Stack mb={2}>
              <Typography variant="h6">
                {formatMessage(messages.disclosureAuth)}
              </Typography>

              <PostRecordAndAttach
                attachments={disclosureAuthConsentWithAttachments?.attachments}
                onBeforeUpload={createAuthConsent}
                resource={disclosureAuthConsent?.getResourceId()}
              />
            </Stack>
          </Grid2>
        </Grid2>
      </CardContent>
    </ContentLoader>
  );
};

export default IdsCard;
